import { useEffect, useRef, useState } from "react";
import { Phone, GripVertical, KanbanSquare, ChevronLeft, ChevronRight } from "lucide-react";
import { STAGES, STAGE_META, Avatar, relTime, slug, answerFor, useIsMobile } from "./crm";

function Card({ lead, onOpen, onDragStart, dragging }) {
  const licensed = answerFor(lead, "licensed");
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", lead.id); onDragStart(lead.id); }}
      onClick={() => onOpen(lead.id)}
      data-testid={`pipeline-card-${lead.id}`}
      className={`group cursor-pointer rounded-lg border border-slate-200 bg-white p-3 shadow-sm transition-all active:scale-[0.99] sm:hover:-translate-y-0.5 sm:hover:border-slate-300 sm:hover:shadow-md ${dragging ? "opacity-40" : ""}`}
    >
      <div className="flex items-start gap-2.5">
        <Avatar name={lead.full_name} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-slate-900">{lead.full_name}</div>
          <div className="truncate text-[11px] text-slate-500">{lead.email}</div>
        </div>
        <GripVertical className="hidden h-4 w-4 shrink-0 text-slate-300 opacity-0 sm:block sm:group-hover:opacity-100" />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {licensed && <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${licensed.toLowerCase().startsWith("yes") ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{licensed.toLowerCase().startsWith("yes") ? "Licensed" : "Unlicensed"}</span>}
        {lead.notes?.length > 0 && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{lead.notes.length} note{lead.notes.length > 1 ? "s" : ""}</span>}
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2.5 text-[11px] text-slate-500">
        <span className="truncate font-medium text-slate-600">{lead.assigned_agent_name || "Unassigned"}</span>
        <span className="font-mono-num shrink-0">{relTime(lead.created_at)}</span>
      </div>
      <a href={`tel:${lead.phone}`} onClick={(e) => e.stopPropagation()} data-testid={`pipeline-card-call-${lead.id}`} className="mt-2.5 flex min-h-[40px] items-center justify-center gap-1.5 rounded-lg bg-sky-50 text-xs font-semibold text-sky-700 active:bg-sky-100 sm:mt-2 sm:hidden sm:min-h-0 sm:justify-start sm:bg-transparent sm:text-[11px] sm:group-hover:flex"><Phone className="h-3.5 w-3.5" /> {lead.phone}</a>
    </div>
  );
}

export default function PipelineView({ leads, onMove, onOpen }) {
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);
  const [active, setActive] = useState(0);
  const isMobile = useIsMobile();
  const scroller = useRef(null);

  const drop = (stage) => (e) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || dragId;
    setOver(null);
    setDragId(null);
    if (id) onMove(id, stage);
  };

  const scrollTo = (i) => {
    const el = scroller.current;
    if (!el) return;
    const col = el.children[0]?.children[i];
    if (col) el.scrollTo({ left: col.offsetLeft - 16, behavior: "smooth" });
  };

  useEffect(() => {
    const el = scroller.current;
    if (!el || !isMobile) return;
    const onScroll = () => {
      const cols = Array.from(el.children[0]?.children || []);
      const center = el.scrollLeft + el.clientWidth / 2;
      let best = 0, dist = Infinity;
      cols.forEach((c, i) => { const d = Math.abs(c.offsetLeft + c.offsetWidth / 2 - center); if (d < dist) { dist = d; best = i; } });
      setActive(best);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [isMobile]);

  return (
    <div className="space-y-4 sm:space-y-5" data-testid="pipeline-view">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">Pipeline</h1>
          <p className="mt-1 text-sm text-slate-500"><span className="sm:hidden">Swipe between stages · </span><span className="hidden sm:inline">Drag recruits between stages. </span>{leads.length} total in pipeline.</p>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600"><KanbanSquare className="h-3.5 w-3.5" /> Board view</span>
      </div>

      {/* Stage switcher (phones) */}
      <div className="sticky top-14 z-20 -mx-4 bg-[#f4f6fa]/95 px-4 py-2 backdrop-blur sm:hidden" data-testid="pipeline-stage-switcher">
        <div className="flex items-center gap-2">
          <button onClick={() => scrollTo(Math.max(0, active - 1))} disabled={active === 0} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 disabled:opacity-30" data-testid="pipeline-prev"><ChevronLeft className="h-4 w-4" /></button>
          <div className="flex flex-1 gap-1.5 overflow-x-auto no-scrollbar">
            {STAGES.map((s, i) => {
              const n = leads.filter((l) => l.status === s).length;
              return (
                <button key={s} onClick={() => scrollTo(i)} data-testid={`pipeline-switch-${slug(s)}`} className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${active === i ? "border-[#0b1528] bg-[#0b1528] text-white" : "border-slate-200 bg-white text-slate-600"}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${STAGE_META[s].dot}`} />{s}<span className="font-mono-num opacity-70">{n}</span>
                </button>
              );
            })}
          </div>
          <button onClick={() => scrollTo(Math.min(STAGES.length - 1, active + 1))} disabled={active === STAGES.length - 1} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 disabled:opacity-30" data-testid="pipeline-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <div ref={scroller} className="-mx-4 snap-x snap-mandatory overflow-x-auto px-4 pb-4 no-scrollbar sm:snap-none sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8" data-testid="pipeline-board">
        <div className="flex min-w-max gap-3 sm:gap-4">
          {STAGES.map((stage) => {
            const items = leads.filter((l) => l.status === stage);
            const m = STAGE_META[stage];
            const isOver = over === stage;
            return (
              <div
                key={stage}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (over !== stage) setOver(stage); }}
                onDragLeave={() => setOver(null)}
                onDrop={drop(stage)}
                data-testid={`pipeline-column-${slug(stage)}`}
                className={`flex w-[calc(100vw-2rem)] shrink-0 snap-center flex-col rounded-xl border transition-colors sm:w-72 ${isOver ? "border-sky-400 bg-sky-50/60" : "border-slate-200 bg-slate-100/70"}`}
              >
                <div className="flex items-center justify-between px-3.5 pt-3.5 pb-2">
                  <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${m.dot}`} />
                    <span className="text-sm font-bold text-slate-800">{stage}</span>
                  </div>
                  <span className="font-mono-num rounded-md bg-white px-2 py-0.5 text-xs font-semibold text-slate-600 border border-slate-200" data-testid={`pipeline-count-${slug(stage)}`}>{items.length}</span>
                </div>
                <p className="px-3.5 pb-2 text-[11px] text-slate-500">{m.hint}</p>
                <div className="flex-1 space-y-2.5 px-2.5 pb-2.5 min-h-[160px]">
                  {items.map((l) => <Card key={l.id} lead={l} onOpen={onOpen} onDragStart={setDragId} dragging={dragId === l.id} />)}
                  {items.length === 0 && <div className={`rounded-lg border border-dashed py-8 text-center text-xs ${isOver ? "border-sky-400 text-sky-600" : "border-slate-300 text-slate-400"}`}><span className="sm:hidden">No recruits in this stage</span><span className="hidden sm:inline">Drop here</span></div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-center text-[11px] text-slate-400 sm:hidden">Tap a recruit to change their stage.</p>
    </div>
  );
}
